import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Save, Loader2, CheckCircle2, XCircle, Circle, ExternalLink } from "lucide-react";
import { PageHeader, StatusBadge } from "../../components/admin/ui";

const PlatformRow = ({ p }) => (
  <div className="flex items-start justify-between gap-4 py-4 border-b border-[#C9A44C]/10 last:border-0">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-serif-display text-lg text-[#F6F0E6]">{p.platform}</span>
        <StatusBadge status={p.status} />
        {p.auto && <span className="text-[10px] tracking-[0.18em] uppercase text-[#C9A44C]">Auto</span>}
      </div>
      <p className="text-xs text-[#D9D9D9]/70 mt-1">{p.notes}</p>
    </div>
    {p.dashboard_url && <a href={p.dashboard_url} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-2 !px-3 !text-xs whitespace-nowrap"><ExternalLink className="h-3.5 w-3.5" /> Open</a>}
  </div>
);

const EnvStatus = ({ label, ok, hint }) => (
  <div className="flex items-start justify-between gap-3 py-3 border-b border-[#C9A44C]/10 last:border-0">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        {ok ? <CheckCircle2 className="h-4 w-4 text-[#C9A44C]" /> : <XCircle className="h-4 w-4 text-[#E8452C]" />}
        <span className="text-sm text-[#F6F0E6]">{label}</span>
      </div>
      {hint && <p className="text-[11px] text-[#D9D9D9]/60 mt-1 pl-6">{hint}</p>}
    </div>
  </div>
);

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, i, p] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/integrations/status"),
        api.get("/admin/payment-platforms"),
      ]);
      setSettings(s.data); setIntegrations(i.data); setPlatforms(p.data.platforms || []);
    } catch { toast.error("Failed to load settings."); }
  };
  useEffect(() => { load(); }, []);

  if (!settings || !integrations) return <div className="text-[#D9D9D9]/60"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/settings", {
        google_calendar_id: settings.google_calendar_id || "",
        google_calendar_timezone: settings.google_calendar_timezone || "America/New_York",
        onesignal_app_id_public: settings.onesignal_app_id_public || "",
      });
      toast.success("Settings saved."); load();
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-settings">
      <PageHeader
        overline="Configuration"
        title={<><span className="italic gold-text">Settings</span></>}
        subtitle="Manage public-facing connections. Secret API keys live in the backend .env file only."
      />

      {/* Payment Platforms */}
      <section className="bento-card !p-7" data-testid="platforms-section">
        <span className="glow-blob" />
        <div className="overline mb-1">Payment Platforms</div>
        <h2 className="font-serif-display text-2xl text-[#F6F0E6] mb-2">Where giving flows in</h2>
        <p className="text-xs text-[#D9D9D9]/60 mb-3">Real connections only. Manual platforms are reconciled via &ldquo;Log Donation&rdquo; on the Donations page.</p>
        <div>
          {platforms.map((p) => <PlatformRow key={p.platform} p={p} />)}
        </div>
      </section>

      {/* Integration Status */}
      <section className="bento-card !p-7" data-testid="env-status">
        <span className="glow-blob" />
        <div className="overline mb-1">Integrations</div>
        <h2 className="font-serif-display text-2xl text-[#F6F0E6] mb-3">Live system status</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          <EnvStatus label="Stripe — card giving" ok={integrations.stripe.configured} hint={integrations.stripe.configured ? `Webhook secret: ${integrations.stripe.webhook_secret_set ? "set" : "optional"}` : "Set STRIPE_API_KEY in backend .env"} />
          <EnvStatus label="PayPal — automatic" ok={integrations.paypal.configured} hint={integrations.paypal.configured ? "Verified" : "Add PAYPAL_CLIENT_ID, PAYPAL_CLIENT_SECRET, PAYPAL_WEBHOOK_ID"} />
          <EnvStatus label="Google Drive — sermon sync" ok={integrations.google_drive.configured} hint={integrations.google_drive.configured ? `Source: ${integrations.google_drive.source_account}` : `Missing: ${integrations.google_drive.missing.join(", ")}`} />
          <EnvStatus label="Google Calendar — public events" ok={integrations.google_calendar.configured} hint={integrations.google_calendar.configured ? integrations.google_calendar.calendar_id : "Set GOOGLE_CALENDAR_ID or fill the field below"} />
          <EnvStatus label="Resend — email notifications" ok={integrations.resend.configured} hint={integrations.resend.configured ? "Outgoing email active" : "Set RESEND_API_KEY"} />
          <EnvStatus label="OneSignal — push notifications" ok={integrations.onesignal.configured} hint={integrations.onesignal.configured ? "Push system live" : "Set ONESIGNAL_APP_ID and ONESIGNAL_REST_API_KEY"} />
        </div>
      </section>

      {/* Public-facing settings */}
      <form onSubmit={save} className="bento-card !p-7 space-y-5">
        <span className="glow-blob" />
        <div className="overline mb-1">Public Display</div>
        <h2 className="font-serif-display text-2xl text-[#F6F0E6]">Calendar & push (public)</h2>
        <div>
          <label className="field-label">Public Google Calendar ID</label>
          <input className="input-fire" placeholder="revivalfireteam@gmail.com or abc@group.calendar.google.com" value={settings.google_calendar_id || ""} onChange={(e) => setSettings({ ...settings, google_calendar_id: e.target.value })} data-testid="setting-calendar-id" />
          <p className="text-[11px] text-[#D9D9D9]/55 mt-1.5">In Google Calendar → Settings → Integrate calendar → copy the Calendar ID. Make sure the calendar&rsquo;s access permission is set to public.</p>
        </div>
        <div>
          <label className="field-label">Timezone</label>
          <input className="input-fire" value={settings.google_calendar_timezone || ""} onChange={(e) => setSettings({ ...settings, google_calendar_timezone: e.target.value })} data-testid="setting-timezone" />
        </div>
        <div>
          <label className="field-label">OneSignal App ID (browser SDK)</label>
          <input className="input-fire" placeholder="00000000-0000-0000-0000-000000000000" value={settings.onesignal_app_id_public || ""} onChange={(e) => setSettings({ ...settings, onesignal_app_id_public: e.target.value })} data-testid="setting-onesignal-id" />
        </div>
        <button type="submit" disabled={saving} className="btn-fire" data-testid="settings-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </form>
    </div>
  );
};

export default Settings;
